import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Paper, Box, Button, Chip, Typography, Alert, Tooltip, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress,
  TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, FormControlLabel, Checkbox, Snackbar, Switch, Tabs, Tab,
  Drawer, List, ListItem, ListItemText
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/History';
import BoltIcon from '@mui/icons-material/Bolt';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BuildIcon from '@mui/icons-material/Build';
import {
  saveNotificationTemplate, resetNotificationTemplate,
  getNotificationTriggers, setTriggerChannel, resetTriggerChannel,
  getTriggerRevisions, getNotificationTemplateDetail, previewNotificationTemplate
} from '../api';
import { useEnv } from '../contexts/EnvContext';
import PageHeader from './common/PageHeader';
import LoadingBlock from './common/LoadingBlock';
import ErrorBlock from './common/ErrorBlock';
import NotificationPublishDialog from './NotificationPublishDialog';

const PLACEHOLDER_RE = /\{\{([A-Za-z][A-Za-z0-9_]*)\}\}/g;

function extractPlaceholders(...parts) {
  const found = new Set();
  for (const part of parts) {
    if (typeof part !== 'string') continue;
    for (const m of part.matchAll(PLACEHOLDER_RE)) found.add(m[1]);
  }
  return [...found];
}

// Rough client-side hint only — the Bidsy backend remains authoritative.
const GSM_RE = /^[A-Za-z0-9 \r\n@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[~\]|€]*$/;
function smsEstimate(body) {
  const gsm = GSM_RE.test(body);
  const single = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67;
  return {
    encoding: gsm ? 'GSM-7' : 'UCS-2 (Unicode)',
    segments: body.length <= single ? 1 : Math.ceil(body.length / multi),
  };
}

const CHANNEL_LABELS = { alert: 'In-app', sms: 'SMS', email: 'Email' };
const CHANNEL_LONG = { alert: 'in-app alert', sms: 'SMS', email: 'email' };

// Column headers pin below the fixed AppBar (64px, matching the layout's
// mt: 8 content offset) while the page scrolls; opaque background so rows
// don't show through.
const STICKY_HEAD_SX = { position: 'sticky', top: 64, zIndex: 2, bgcolor: 'background.paper' };
// Sticky cells mis-composite in Chrome inside collapsed-border tables (row
// text ghosts through at the seam) — separate borders avoids it.
const STICKY_TABLE_SX = { tableLayout: 'fixed', borderCollapse: 'separate' };

// Server categories are slugs; display names live here so the grouping
// order can stay server-driven while the headings stay human.
const CATEGORY_LABELS = {
  auth: 'Sign-in & account',
  bid: 'Bidding',
  connection: 'Connections',
  payment: 'Payments & cash-outs',
  token: 'Tokens',
  referral: 'Referrals',
  chat: 'Chat',
  creator: 'Creator onboarding & profile',
  video: 'Video calls',
};

// When a trigger sends (served as `timing` on every trigger/row by the
// reporting backend, from config/notificationTiming.js). Filter values and
// chip presentation are keyed by the served `mode`.
const TIMING_MODES = {
  on_demand: { label: 'On demand', color: 'default', Icon: BoltIcon, hint: 'Sent right away, in direct response to an action.' },
  scheduled: { label: 'Scheduled', color: 'info', Icon: ScheduleIcon, hint: 'Sent by a scheduled job or a timed delay, not at the moment of the action.' },
  manual: { label: 'Manual', color: 'warning', Icon: BuildIcon, hint: 'Only sent when someone runs a script by hand.' },
};

const timingSearchText = (timing) =>
  timing
    ? [TIMING_MODES[timing.mode]?.label, timing.summary, timing.schedule, timing.job, ...(timing.details || [])]
    : [];

const matchesTimingFilter = (timing, timingFilter) =>
  timingFilter === 'all' || timing?.mode === timingFilter;

// The editor's cell shape, mirrored from the backend report assembly so the
// editor internals work identically for matrix details and legacy cells.
function detailToCell(detail) {
  return {
    key: detail.template_key,
    channel: detail.channel,
    protected: detail.protected,
    has_override: detail.override !== null,
    enabled: detail.override ? detail.override.enabled : null,
    source: detail.effective.source,
    subject: detail.effective.subject,
    body: detail.effective.body,
    override_subject: detail.override?.subject ?? null,
    override_body: detail.override?.body ?? null,
    default_subject: detail.default?.subject ?? null,
    default_body: detail.default?.body ?? null,
    variables: detail.variables || [],
    required_vars: detail.required_vars || [],
    optout_phrases: detail.optout_phrases || [],
    updated_by: detail.override?.updated_by ?? null,
    updated_at: detail.override?.updated_at ?? null,
  };
}

// Tab labels for multi-variant channels: the part of each key that differs
// (e.g. sms.bid.kicked.minimum_bid_raised → minimum_bid_raised).
function variantLabels(templateKeys) {
  if (templateKeys.length < 2) return templateKeys.map((k) => k);
  const split = templateKeys.map((k) => k.split('.'));
  let common = 0;
  while (split.every((parts) => parts.length > common + 1 && parts[common] === split[0][common])) {
    common += 1;
  }
  return split.map((parts) => parts.slice(common).join('.'));
}

function TemplateText({ text, sx }) {
  if (!text) return null;
  const parts = String(text).split(/(\{\{[A-Za-z][A-Za-z0-9_]*\}\})/g);
  return (
    <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'block', ...sx }}>
      {parts.map((p, i) =>
        /^\{\{[A-Za-z][A-Za-z0-9_]*\}\}$/.test(p) ? (
          <Box key={i} component="span" sx={{ color: 'primary.main', fontFamily: 'monospace', fontSize: '0.85em' }}>{p}</Box>
        ) : (
          p
        )
      )}
    </Typography>
  );
}

/* ------------------------------------------------------------------ *
 * "When it sends" cell: mode chip + one-line summary, with the literal
 * schedule, the sending job, and the finer points behind a toggle so the
 * table stays scannable.
 * ------------------------------------------------------------------ */
function TimingCell({ timing }) {
  const [open, setOpen] = useState(false);
  if (!timing) {
    return (
      <Tooltip title="No timing has been written up for this trigger yet (backend/config/notificationTiming.js).">
        <Chip label="Not documented yet" size="small" variant="outlined" />
      </Tooltip>
    );
  }
  const mode = TIMING_MODES[timing.mode];
  const hasMore = Boolean(timing.schedule || timing.job || timing.details?.length);
  return (
    <Box>
      {mode && (
        <Tooltip title={mode.hint}>
          <Chip icon={<mode.Icon />} label={mode.label} size="small" color={mode.color} variant="outlined" sx={{ mb: 0.5 }} />
        </Tooltip>
      )}
      <Typography variant="body2">{timing.summary}</Typography>
      {hasMore && (
        <Button
          size="small"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: '0.75rem' }}
        >
          {open ? 'Hide details' : 'Details'}
        </Button>
      )}
      {open && (
        <Box sx={{ mt: 0.5 }}>
          {timing.schedule && (
            <Typography variant="caption" component="div" sx={{ wordBreak: 'break-word' }}>
              <strong>Schedule:</strong>{' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>{timing.schedule}</Box>
            </Typography>
          )}
          {timing.job && (
            <Typography variant="caption" component="div" sx={{ wordBreak: 'break-word' }}>
              <strong>Sent by:</strong>{' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>{timing.job}</Box>
            </Typography>
          )}
          {timing.details?.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
              {timing.details.map((d) => (
                <Typography key={d} variant="caption" component="li" color="text.secondary">{d}</Typography>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ *
 * Legacy read-only cell — used by the fallback view (environments whose
 * Bidsy build doesn't serve the trigger matrix yet, production today).
 * ------------------------------------------------------------------ */
function LegacyCellContent({ cell }) {
  if (!cell) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  const disabledRow = cell.has_override && cell.enabled === false;
  const customized = cell.has_override && cell.differs_from_default && cell.enabled !== false;
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: cell.protected || customized || disabledRow ? 0.5 : 0 }}>
        {cell.protected && (
          <Tooltip title="Protected template (sign-in/payment copy) — extra confirmation required to edit">
            <Chip icon={<LockIcon />} label="Protected" size="small" variant="outlined" />
          </Tooltip>
        )}
        {customized && (
          <Tooltip title={`Customized copy${cell.updated_by ? ` — last edited by ${cell.updated_by}` : ''}${cell.updated_at ? ` on ${new Date(cell.updated_at).toLocaleString()}` : ''}`}>
            <Chip label="Customized" size="small" color="primary" variant="outlined" />
          </Tooltip>
        )}
        {disabledRow && (
          <Tooltip title="The saved custom copy is switched off, so Bidsy's built-in default is being sent. The notification itself still goes out.">
            <Chip label="Custom copy off — default in use" size="small" color="warning" variant="outlined" />
          </Tooltip>
        )}
      </Box>
      {cell.channel === 'email' && (
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }} component="div">
          Subject: <TemplateText text={cell.subject} sx={{ display: 'inline', fontWeight: 400 }} />
        </Typography>
      )}
      <TemplateText text={cell.body} />
    </Box>
  );
}

function LegacyReportView({ data, search, channelFilter, timingFilter }) {
  const rows = data?.rows || [];
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (channelFilter !== 'all' && !row.cells[channelFilter]) return false;
      if (!matchesTimingFilter(row.timing, timingFilter)) return false;
      if (!q) return true;
      const hay = [
        row.trigger,
        ...timingSearchText(row.timing),
        ...['sms', 'alert', 'email'].flatMap((ch) => {
          const c = row.cells[ch];
          return c ? [c.key, c.subject, c.body] : [];
        }),
      ].filter(Boolean).join('\n').toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, channelFilter, timingFilter]);

  let lastCategory = null;
  if (!filteredRows.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
        No notifications match your search.
      </Typography>
    );
  }
  return (
    <Table size="small" sx={STICKY_TABLE_SX}>
      <TableHead>
        <TableRow>
          {['Trigger', 'When it sends', 'App alert', 'SMS', 'Email'].map((c, i) => (
            <TableCell key={c} sx={{ ...STICKY_HEAD_SX, fontWeight: 'bold', width: ['20%', '18%'][i] }}>{c}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {filteredRows.map((row) => {
          const showCategory = row.category !== lastCategory;
          lastCategory = row.category;
          return (
            <React.Fragment key={row.id}>
              {showCategory && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ bgcolor: 'action.selected', fontWeight: 700 }}>
                    {row.category}
                  </TableCell>
                </TableRow>
              )}
              <TableRow sx={{ '& > td': { verticalAlign: 'top', py: 1.5 } }}>
                <TableCell><Typography variant="body2">{row.trigger}</Typography></TableCell>
                <TableCell><TimingCell timing={row.timing} /></TableCell>
                <TableCell><LegacyCellContent cell={row.cells.alert} /></TableCell>
                <TableCell><LegacyCellContent cell={row.cells.sms} /></TableCell>
                <TableCell><LegacyCellContent cell={row.cells.email} /></TableCell>
              </TableRow>
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}

/* ------------------------------------------------------------------ *
 * Matrix cell: one trigger × channel. Switch + state chips; the whole
 * cell opens the channel editor.
 * ------------------------------------------------------------------ */
function MatrixCell({ trigger, channel, editable, onOpenEditor, onToggle, onHistory, busy }) {
  const info = trigger.channels?.[channel];
  if (!info || info.supported === false) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  const off = !info.enabled;
  const multi = (info.template_keys || []).length > 1;
  // Priority routing metadata (trigger-level channel_priority, e.g.
  // ['email','sms']): sends go to the first listed channel whose delivery
  // gate passes; the rest are skipped. Channels not listed are unaffected.
  const priorityIndex = trigger.channel_priority ? trigger.channel_priority.indexOf(channel) : -1;

  return (
    <Box
      onClick={editable ? () => onOpenEditor(trigger, channel, 'edit') : undefined}
      sx={{
        cursor: editable ? 'pointer' : 'default',
        borderRadius: 1,
        p: 0.5,
        m: -0.5,
        '&:hover': editable ? { bgcolor: 'action.hover' } : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Tooltip
          title={
            info.locked
              ? 'Transactional — cannot be disabled'
              : info.enabled_source === 'setting'
                ? `Set by ${info.updated_by || 'admin'}${info.updated_at ? ` on ${new Date(info.updated_at).toLocaleString()}` : ''}`
                : 'Using the registry default state'
          }
        >
          <span onClick={(e) => e.stopPropagation()}>
            <Switch
              size="small"
              checked={Boolean(info.enabled)}
              disabled={!editable || info.locked || busy}
              onChange={() => onToggle(trigger, channel, info)}
              inputProps={{ 'aria-label': `${trigger.label} ${CHANNEL_LABELS[channel]} on/off` }}
            />
          </span>
        </Tooltip>
        {info.locked && (
          <Tooltip title="Transactional — cannot be disabled">
            <LockIcon fontSize="small" color="disabled" />
          </Tooltip>
        )}
        {off && !info.locked && (
          <Chip label="OFF" size="small" color="error" variant="outlined" sx={{ opacity: 0.8 }} />
        )}
        {info.enabled && info.has_override && (
          <Tooltip title="Has custom copy">
            <Chip label="custom" size="small" color="primary" variant="outlined" />
          </Tooltip>
        )}
        {!info.enabled && info.has_override && (
          <Tooltip title="Custom copy is saved and will be used when this channel is turned on">
            <Chip label="custom" size="small" variant="outlined" sx={{ opacity: 0.7 }} />
          </Tooltip>
        )}
        <Tooltip title={`${CHANNEL_LABELS[channel]} history`}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onHistory(trigger, channel); }}
            aria-label={`${CHANNEL_LABELS[channel]} history for ${trigger.label}`}
            sx={{ ml: 'auto', opacity: 0.55, '&:hover': { opacity: 1 } }}
          >
            <HistoryIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.25, opacity: off ? 0.6 : 1 }}>
        {channel === 'sms' && info.delivery === 'offline_only' && (
          <Tooltip title="Sent only when the user is not active in the app">
            <Chip label="offline only" size="small" variant="outlined" />
          </Tooltip>
        )}
        {priorityIndex >= 0 && (
          <Tooltip
            title={`Delivery priority: ${trigger.channel_priority.map((c) => CHANNEL_LABELS[c] || c).join(' → ')} — each send goes to the first of these channels that can deliver; the rest are skipped.`}
          >
            <Chip
              label={`${priorityIndex + 1} · ${priorityIndex === 0 ? 'primary' : 'fallback'}`}
              size="small"
              variant="outlined"
              sx={{ opacity: 0.8 }}
            />
          </Tooltip>
        )}
        {multi && (
          <Typography variant="caption" color="text.secondary">
            {info.template_keys.length} variants
          </Typography>
        )}
        {off && !info.locked && editable && (
          <Button
            size="small"
            onClick={(e) => { e.stopPropagation(); onOpenEditor(trigger, channel, 'enable'); }}
            sx={{ py: 0, minWidth: 0, textTransform: 'none' }}
          >
            Add / Turn on
          </Button>
        )}
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ *
 * Channel editor dialog. Edits one template key at a time; multi-variant
 * channels get one tab per key in the channel's template_keys array. The
 * single channel switch governs all of a channel's keys — copy edits go
 * through the legacy per-key template routes.
 * ------------------------------------------------------------------ */
// Pure per-key helpers so the multi-variant flows can reason about EVERY
// key's draft, not just the visible tab's.
function cellIsDirty(cell, draft) {
  if (!cell || !draft) return false;
  return draft.body !== (cell.override_body ?? cell.body ?? '')
    || (cell.channel === 'email' && draft.subject !== (cell.override_subject ?? cell.subject ?? ''));
}

// Draft validation (hints — upstream validation remains authoritative)
function problemsFor(cell, draft) {
  if (!cell || !draft) return { errors: [], optoutMissing: [] };
  const isEmail = cell.channel === 'email';
  const errors = [];
  if (!draft.body.trim()) errors.push('Body is required.');
  if (isEmail && !draft.subject.trim()) errors.push('Email subject is required.');
  const unknown = extractPlaceholders(draft.body, isEmail ? draft.subject : null)
    .filter((v) => !(cell.variables || []).includes(v));
  for (const v of unknown) errors.push(`Unknown placeholder: {{${v}}} — allowed: ${(cell.variables || []).join(', ') || 'none'}`);
  const combined = isEmail ? `${draft.subject}\n${draft.body}` : draft.body;
  const missingRequired = (cell.required_vars || []).filter((v) => !combined.includes(`{{${v}}}`));
  for (const v of missingRequired) errors.push(`Missing required placeholder: {{${v}}}`);
  const optoutMissing = (cell.optout_phrases || []).filter((p) => !draft.body.includes(p));
  return { errors, optoutMissing };
}

function ChannelEditorDialog({ editing, env, onClose, onMutated, onRefresh }) {
  const { triggerKey, triggerLabel, channel, templateKeys, channelInfo, intent } = editing;
  const [activeKey, setActiveKey] = useState(templateKeys[0]);
  const [channelState, setChannelState] = useState(channelInfo);
  const [details, setDetails] = useState({});   // key → {status, cell, error}
  const [drafts, setDrafts] = useState({});     // key → {body, subject}
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [enableConfirmOpen, setEnableConfirmOpen] = useState(false);
  const [resetCopyConfirmOpen, setResetCopyConfirmOpen] = useState(false);
  const [resetStateConfirmOpen, setResetStateConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [ackProtected, setAckProtected] = useState(false);
  const [ackOptout, setAckOptout] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [preview, setPreview] = useState(null); // {loading, data, error}
  const previewSeq = useRef(0);
  const bodyInputRef = useRef(null);

  const labels = useMemo(() => variantLabels(templateKeys), [templateKeys]);

  // Details are written under the key that was fetched, so a tab switch
  // mid-flight can never strand a tab: the result (or error) always lands,
  // and `details` in the deps re-triggers the fetch when an entry is
  // invalidated after a save.
  useEffect(() => {
    if (details[activeKey]) return;
    const key = activeKey;
    setDetails((d) => ({ ...d, [key]: { status: 'loading' } }));
    (async () => {
      try {
        const res = await getNotificationTemplateDetail(key);
        const cell = detailToCell(res?.data || {});
        setDetails((d) => ({ ...d, [key]: { status: 'ready', cell } }));
        setDrafts((prev) => prev[key] ? prev : {
          ...prev,
          [key]: {
            body: cell.override_body ?? cell.body ?? '',
            subject: cell.channel === 'email' ? (cell.override_subject ?? cell.subject ?? '') : '',
          },
        });
      } catch (e) {
        console.error(e);
        setDetails((d) => ({
          ...d,
          [key]: { status: 'error', error: e?.response?.data?.message || 'Failed to load template' },
        }));
      }
    })();
    // eslint-disable-next-line
  }, [activeKey, details]);

  const entry = details[activeKey];
  const cell = entry?.status === 'ready' ? entry.cell : null;
  const draft = drafts[activeKey] || { body: '', subject: '' };
  const isEmail = channel === 'email';

  const setDraft = (patch) => setDrafts((d) => ({ ...d, [activeKey]: { ...(d[activeKey] || draft), ...patch } }));

  const dirtyKeys = useMemo(
    () => templateKeys.filter((k) => details[k]?.status === 'ready' && cellIsDirty(details[k].cell, drafts[k])),
    [templateKeys, details, drafts]
  );
  const dirtyWithErrors = dirtyKeys.filter((k) => problemsFor(details[k].cell, drafts[k]).errors.length > 0);
  const anyDirtyProtected = dirtyKeys.some((k) => details[k].cell.protected);
  const dirtyOptoutMissing = [...new Set(dirtyKeys.flatMap((k) => problemsFor(details[k].cell, drafts[k]).optoutMissing))];

  const invalidateDetails = (keys) => setDetails((d) => {
    const next = { ...d };
    for (const k of keys) delete next[k];
    return next;
  });

  const insertVariable = (name) => {
    const token = `{{${name}}}`;
    const input = bodyInputRef.current;
    if (input && typeof input.selectionStart === 'number') {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const next = draft.body.slice(0, start) + token + draft.body.slice(end);
      setDraft({ body: next });
      requestAnimationFrame(() => {
        input.focus();
        input.selectionStart = input.selectionEnd = start + token.length;
      });
    } else {
      setDraft({ body: draft.body + token });
    }
  };

  const draftProblems = useMemo(() => problemsFor(cell, draft), [cell, draft]);

  const smsInfo = cell && channel === 'sms' && draft.body ? smsEstimate(draft.body) : null;
  const isDirty = cellIsDirty(cell, draft);
  // A switched-off stored copy can be re-enabled by saving it unchanged
  // (the save always writes enabled: true on the template row).
  const reEnabling = Boolean(cell?.has_override && cell?.enabled === false);
  const channelOff = Boolean(channelState) && !channelState.enabled && !channelState.locked;

  // "Save copy" persists EVERY dirty variant, not just the visible tab.
  const keysToSave = dirtyKeys.length ? dirtyKeys : (reEnabling ? [activeKey] : []);

  const resetAcks = () => { setAckProtected(false); setAckOptout(false); };

  const requestClose = () => {
    if (saving) return;
    if (dirtyKeys.length) setDiscardConfirmOpen(true);
    else onClose();
  };

  const saveCopyFor = async (key) => {
    const c = details[key].cell;
    const dr = drafts[key];
    const payload = {
      body: dr.body,
      expected_updated_at: c.updated_at ?? null,
    };
    if (c.channel === 'email') payload.subject = dr.subject;
    if (c.protected) payload.confirm_protected = true;
    return saveNotificationTemplate(key, payload);
  };

  // Shared failure handling for the sequential save flows: keys already
  // saved upstream get their cached detail invalidated (a refetch brings the
  // fresh updated_at, so a retry can't 409 on a stale concurrency check) and
  // the matrix refreshes so their new 'custom' state shows.
  const handleSaveFlowError = (e, savedKeys, failedKey, fallbackMessage) => {
    console.error(e);
    const resp = e?.response;
    const extra = resp?.data?.validation_errors?.length ? ` (${resp.data.validation_errors.join('; ')})` : '';
    if (savedKeys.length) {
      invalidateDetails(savedKeys);
      onRefresh();
    }
    setSaveError(`${failedKey ? `${failedKey}: ` : ''}${resp?.data?.message || fallbackMessage}${extra}`);
  };

  const handleSaveCopy = async () => {
    if (!keysToSave.length) return;
    const saved = [];
    try {
      setSaving(true);
      setSaveError(null);
      let advisory = null;
      for (const k of keysToSave) {
        const res = await saveCopyFor(k);
        saved.push(k);
        advisory = res?.data?.sms_advisory ?? advisory;
      }
      onMutated(`Saved ${keysToSave.join(', ')} to ${env}${advisory ? ` — ${advisory.encoding}, ~${advisory.estimated_segments} segment${advisory.estimated_segments === 1 ? '' : 's'}` : ''}`);
      onClose();
    } catch (e) {
      setSaveConfirmOpen(false);
      handleSaveFlowError(e, saved, keysToSave[saved.length], 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleEnableWithCopy = async () => {
    if (!cell || !triggerKey) return;
    const saved = [];
    try {
      setSaving(true);
      setSaveError(null);
      // Content PUTs first, but only for variants the admin actually edited —
      // an untouched editor just turns the channel on over the defaults.
      for (const k of dirtyKeys) {
        await saveCopyFor(k);
        saved.push(k);
      }
      await setTriggerChannel(triggerKey, channel, { send_enabled: true });
      onMutated(`${CHANNEL_LABELS[channel]} turned on for "${triggerLabel}"${saved.length ? ' with your copy' : ''}`);
      onClose();
    } catch (e) {
      setEnableConfirmOpen(false);
      handleSaveFlowError(e, saved, saved.length < dirtyKeys.length ? dirtyKeys[saved.length] : null, 'Failed to enable the channel');
    } finally {
      setSaving(false);
    }
  };

  const handleResetCopy = async () => {
    if (!cell) return;
    try {
      setSaving(true);
      setSaveError(null);
      const payload = cell.protected ? { confirm_protected: true } : {};
      await resetNotificationTemplate(activeKey, payload);
      // Refetch only the reset key so its editor returns to the built-in
      // default while drafts on every other variant stay intact.
      setDrafts((current) => {
        const next = { ...current };
        delete next[activeKey];
        return next;
      });
      invalidateDetails([activeKey]);
      setResetCopyConfirmOpen(false);
      onMutated(`${activeKey} reset to Bidsy's built-in default`);
    } catch (e) {
      console.error(e);
      setResetCopyConfirmOpen(false);
      setSaveError(e?.response?.data?.message || 'Failed to reset template');
    } finally {
      setSaving(false);
    }
  };

  const handleResetState = async () => {
    if (!triggerKey) return;
    try {
      setSaving(true);
      setSaveError(null);
      const res = await resetTriggerChannel(triggerKey, channel);
      // Channel state is independent from copy. Keep the editor and every
      // draft open, and update the local switch metadata from the reset.
      setChannelState((current) => ({ ...current, ...(res?.data || {}) }));
      setResetStateConfirmOpen(false);
      onMutated(`${CHANNEL_LABELS[channel]} on/off state for "${triggerLabel}" reset to the registry default`);
    } catch (e) {
      console.error(e);
      setResetStateConfirmOpen(false);
      setSaveError(e?.response?.data?.message || 'Failed to reset the channel state');
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    const seq = ++previewSeq.current;
    setPreview({ loading: true });
    try {
      const payload = isDirty
        ? { use: 'candidate', body: draft.body, ...(isEmail ? { subject: draft.subject } : {}) }
        : { use: 'effective' };
      const res = await previewNotificationTemplate(activeKey, payload);
      if (seq === previewSeq.current) setPreview({ loading: false, data: res?.data });
    } catch (e) {
      console.error(e);
      if (seq === previewSeq.current) setPreview({ loading: false, error: e?.response?.data?.message || 'Preview failed' });
    }
  };

  const closePreview = () => {
    previewSeq.current += 1;
    setPreview(null);
  };

  return (
    <>
      <Dialog open onClose={requestClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {intent === 'enable' ? 'Add content & turn on' : 'Edit'} {CHANNEL_LONG[channel]} — {triggerLabel}
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip label="DEVELOPMENT" color="warning" size="small" />
            <Chip label={activeKey} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
            {cell?.protected && <Chip icon={<LockIcon />} label="Protected" size="small" variant="outlined" />}
            {channelState && !channelState.enabled && <Chip label="Channel OFF" size="small" color="error" variant="outlined" />}
          </Box>
        </DialogTitle>
        {/* MUI zeroes DialogContent's top padding after a DialogTitle; keep enough so the
            first field's floating label isn't clipped by the scroll container. */}
        <DialogContent sx={{ pt: '12px !important' }}>
          {templateKeys.length > 1 && (
            <Tabs
              value={activeKey}
              onChange={(e, v) => { setSaveError(null); setActiveKey(v); }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              {templateKeys.map((k, i) => (
                <Tab
                  key={k}
                  value={k}
                  label={dirtyKeys.includes(k) ? `${labels[i]} •` : labels[i]}
                  sx={{ textTransform: 'none', fontFamily: 'monospace' }}
                />
              ))}
            </Tabs>
          )}

          {!entry || entry.status === 'loading' ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : entry.status === 'error' ? (
            <Alert severity="error">{entry.error}</Alert>
          ) : (
            <>
              {isEmail && (
                <TextField
                  label="Subject"
                  fullWidth
                  size="small"
                  value={draft.subject}
                  onChange={(e) => setDraft({ subject: e.target.value })}
                  sx={{ mb: 2 }}
                />
              )}
              <TextField
                label={isEmail ? 'Body (plain text)' : 'Body'}
                fullWidth
                multiline
                minRows={isEmail ? 6 : 3}
                value={draft.body}
                onChange={(e) => setDraft({ body: e.target.value })}
                inputRef={bodyInputRef}
              />
              {isEmail && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Text only for now — the HTML version of this email is kept exactly as it is today.
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Alerts and SMS render at send time — edits affect future sends only.
              </Typography>

              {(cell.variables || []).length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Placeholders (click to insert{cell.required_vars?.length ? '; * = required' : ''}):
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {cell.variables.map((v) => (
                      <Chip
                        key={v}
                        label={`{{${v}}}${(cell.required_vars || []).includes(v) ? ' *' : ''}`}
                        size="small"
                        onClick={() => insertVariable(v)}
                        sx={{ fontFamily: 'monospace' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {smsInfo && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {draft.body.length} characters • {smsInfo.encoding} • ~{smsInfo.segments} SMS segment{smsInfo.segments === 1 ? '' : 's'}
                </Typography>
              )}

              {draftProblems.errors.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {draftProblems.errors.map((er, i) => (
                    <Typography key={i} variant="body2">{er}</Typography>
                  ))}
                </Alert>
              )}
              {draftProblems.optoutMissing.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This message normally includes the opt-out wording{' '}
                  {draftProblems.optoutMissing.map((p) => `“${p}”`).join(', ')} and your draft doesn't.
                  You'll be asked to confirm before saving.
                </Alert>
              )}
              {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
              {dirtyWithErrors.filter((k) => k !== activeKey).length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Another edited variant has validation errors:{' '}
                  {dirtyWithErrors.filter((k) => k !== activeKey).map((k) => labels[templateKeys.indexOf(k)]).join(', ')}.
                  Fix it before saving.
                </Alert>
              )}

              {channelState && channelState.enabled_source === 'setting' && !channelState.locked && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  This channel's on/off state was set by an admin.{' '}
                  <Button size="small" onClick={() => setResetStateConfirmOpen(true)} disabled={saving} sx={{ py: 0, textTransform: 'none' }}>
                    Reset on/off to default ({channelState.default_enabled ? 'on' : 'off'})
                  </Button>
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            color="error"
            onClick={() => setResetCopyConfirmOpen(true)}
            disabled={saving || !cell?.has_override}
          >
            Reset copy to default…
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={requestClose} disabled={saving}>Cancel</Button>
            <Button onClick={openPreview} disabled={saving || !cell}>
              Preview
            </Button>
            <Button
              variant={channelOff ? 'outlined' : 'contained'}
              onClick={() => { resetAcks(); setSaveConfirmOpen(true); }}
              disabled={saving || !cell || !keysToSave.length || dirtyWithErrors.length > 0}
            >
              Save copy…
            </Button>
            {channelOff && triggerKey && (
              <Button
                variant="contained"
                onClick={() => { resetAcks(); setEnableConfirmOpen(true); }}
                disabled={saving || !cell || dirtyWithErrors.length > 0}
              >
                Enable with this copy…
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* ------------------- Save-copy confirmation ------------------- */}
      <Dialog open={saveConfirmOpen} onClose={saving ? undefined : () => setSaveConfirmOpen(false)} maxWidth="sm" fullWidth>
        {cell && (
          <>
            <DialogTitle>Save to DEVELOPMENT?</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  This saves{' '}
                  {keysToSave.map((k, i) => (
                    <React.Fragment key={k}>
                      {i > 0 && ', '}
                      <Box component="span" sx={{ fontFamily: 'monospace' }}>{k}</Box>
                    </React.Fragment>
                  ))}{' '}
                  as the live copy on the <strong>development</strong> environment (dev.bidsy.app).
                  New notifications will use it within about a minute.
                </Typography>
                {channelOff && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    The {CHANNEL_LONG[channel]} channel stays <strong>off</strong> — you're authoring copy for a later turn-up.
                  </Typography>
                )}
                {reEnabling && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    The stored custom copy is currently switched off — saving turns it back on.
                  </Typography>
                )}
                {(anyDirtyProtected || (!dirtyKeys.length && cell.protected)) && (
                  <FormControlLabel
                    control={<Checkbox checked={ackProtected} onChange={(e) => setAckProtected(e.target.checked)} />}
                    label="I understand this is protected sign-in/payment copy and mistakes can lock users out or misstate payouts."
                  />
                )}
                {dirtyOptoutMissing.length > 0 && (
                  <FormControlLabel
                    control={<Checkbox checked={ackOptout} onChange={(e) => setAckOptout(e.target.checked)} />}
                    label={`I understand this removes the opt-out wording (${dirtyOptoutMissing.join(', ')}) and I accept the compliance impact.`}
                  />
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSaveConfirmOpen(false)} disabled={saving}>Back</Button>
              <Button
                variant="contained"
                onClick={handleSaveCopy}
                disabled={
                  saving ||
                  ((anyDirtyProtected || (!dirtyKeys.length && cell.protected)) && !ackProtected) ||
                  (dirtyOptoutMissing.length > 0 && !ackOptout)
                }
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ------------------- Enable-with-copy confirmation ------------------- */}
      <Dialog open={enableConfirmOpen} onClose={saving ? undefined : () => setEnableConfirmOpen(false)} maxWidth="sm" fullWidth>
        {cell && (
          <>
            <DialogTitle>Turn on the {CHANNEL_LONG[channel]} for “{triggerLabel}”?</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  New {CHANNEL_LONG[channel]} notifications for this trigger will start sending on the
                  {' '}<strong>development</strong> environment.
                </Typography>
                {dirtyKeys.length ? (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Your copy edits to{' '}
                    {dirtyKeys.map((k, i) => (
                      <React.Fragment key={k}>
                        {i > 0 && ', '}
                        <Box component="span" sx={{ fontFamily: 'monospace' }}>{k}</Box>
                      </React.Fragment>
                    ))}{' '}
                    will be saved first.
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    The registry default copy will be used (you haven't changed it).
                  </Typography>
                )}
                {templateKeys.length > 1 && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    The switch governs all {templateKeys.length} variants of this channel.
                  </Typography>
                )}
                {anyDirtyProtected && (
                  <FormControlLabel
                    control={<Checkbox checked={ackProtected} onChange={(e) => setAckProtected(e.target.checked)} />}
                    label="I understand this is protected sign-in/payment copy and mistakes can lock users out or misstate payouts."
                  />
                )}
                {dirtyOptoutMissing.length > 0 && (
                  <FormControlLabel
                    control={<Checkbox checked={ackOptout} onChange={(e) => setAckOptout(e.target.checked)} />}
                    label={`I understand this removes the opt-out wording (${dirtyOptoutMissing.join(', ')}) and I accept the compliance impact.`}
                  />
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEnableConfirmOpen(false)} disabled={saving}>Back</Button>
              <Button
                variant="contained"
                onClick={handleEnableWithCopy}
                disabled={
                  saving ||
                  (anyDirtyProtected && !ackProtected) ||
                  (dirtyOptoutMissing.length > 0 && !ackOptout)
                }
              >
                {saving ? 'Turning on…' : 'Turn on'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ------------------- Reset-copy confirmation ------------------- */}
      <Dialog open={resetCopyConfirmOpen} onClose={saving ? undefined : () => setResetCopyConfirmOpen(false)} maxWidth="sm" fullWidth>
        {cell && (
          <>
            <DialogTitle>Reset to Bidsy's built-in default?</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  This removes the saved custom copy for{' '}
                  <Box component="span" sx={{ fontFamily: 'monospace' }}>{activeKey}</Box> on <strong>development</strong>.
                  The copy that ships with the Bidsy code becomes active again.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This only changes which wording is used — the channel's on/off switch is separate.
                  Edit history is kept on the Bidsy side.
                </Typography>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setResetCopyConfirmOpen(false)} disabled={saving}>Back</Button>
              <Button color="error" variant="contained" onClick={handleResetCopy} disabled={saving}>
                {saving ? 'Resetting…' : 'Reset to default'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ------------------- Reset-state confirmation ------------------- */}
      <Dialog open={resetStateConfirmOpen} onClose={saving ? undefined : () => setResetStateConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset the on/off state?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2">
              This removes the admin-set on/off state for the {CHANNEL_LONG[channel]} of “{triggerLabel}” on
              {' '}<strong>development</strong> and returns it to the registry default
              ({channelState?.default_enabled ? 'on' : 'off'}). Saved copy is not touched.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetStateConfirmOpen(false)} disabled={saving}>Back</Button>
          <Button color="error" variant="contained" onClick={handleResetState} disabled={saving}>
            {saving ? 'Resetting…' : 'Reset state'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------- Discard-unsaved confirmation ------------------- */}
      <Dialog open={discardConfirmOpen} onClose={() => setDiscardConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Discard unsaved copy edits?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2">
              You have unsaved edits on{' '}
              {dirtyKeys.map((k, i) => (
                <React.Fragment key={k}>
                  {i > 0 && ', '}
                  <Box component="span" sx={{ fontFamily: 'monospace' }}>{k}</Box>
                </React.Fragment>
              ))}. Closing discards them.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardConfirmOpen(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={onClose}>Discard</Button>
        </DialogActions>
      </Dialog>

      {/* ------------------- Preview dialog ------------------- */}
      <Dialog open={Boolean(preview)} onClose={closePreview} maxWidth="sm" fullWidth>
        <DialogTitle>
          Preview — {isDirty ? 'your draft' : 'current effective copy'}
          <Box sx={{ mt: 0.5 }}>
            <Chip label={activeKey} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
          </Box>
        </DialogTitle>
        <DialogContent>
          {preview?.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : preview?.error ? (
            <Alert severity="error">{preview.error}</Alert>
          ) : preview?.data ? (
            <>
              {isEmail && preview.data.rendered?.subject != null && (
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Subject: <Box component="span" sx={{ fontWeight: 400 }}>{preview.data.rendered.subject}</Box>
                </Typography>
              )}
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                <Typography variant="body2">{preview.data.rendered?.text}</Typography>
              </Paper>
              {preview.data.sms_advisory && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {preview.data.sms_advisory.encoding} • ~{preview.data.sms_advisory.estimated_segments} SMS segment{preview.data.sms_advisory.estimated_segments === 1 ? '' : 's'}
                </Typography>
              )}
              {(preview.data.missing_variables?.length > 0 || preview.data.unknown_placeholders?.length > 0 || preview.data.required_var_violations?.length > 0) && (
                <Alert severity="warning">
                  {preview.data.missing_variables?.length > 0 && (
                    <Typography variant="body2">Missing variables: {preview.data.missing_variables.join(', ')}</Typography>
                  )}
                  {preview.data.unknown_placeholders?.length > 0 && (
                    <Typography variant="body2">Unknown placeholders: {preview.data.unknown_placeholders.join(', ')}</Typography>
                  )}
                  {preview.data.required_var_violations?.length > 0 && (
                    <Typography variant="body2">Missing required placeholders: {preview.data.required_var_violations.join(', ')}</Typography>
                  )}
                </Alert>
              )}
              {isEmail && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Rendered from the plain-text body — the HTML version renders on the Bidsy side.
                </Typography>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * History drawer: per-trigger revisions — on/off changes (kind 'setting')
 * interleaved with copy changes (kind 'content', carrying the exact saved
 * subject/body). An optional channel narrows the history to one route.
 * Rows without a kind come from a pre-content-history Bidsy build and are
 * treated as setting rows.
 * ------------------------------------------------------------------ */
const CONTENT_ACTION_CHIPS = {
  update: { label: 'copy updated', color: 'info' },
  reset: { label: 'copy reset', color: 'warning' },
  seed: { label: 'copy seeded', color: 'default' },
};

function HistoryDrawer({ trigger, channel, onClose }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    (async () => {
      try {
        const res = await getTriggerRevisions(trigger.trigger_key, {
          limit: 50,
          ...(channel ? { channel } : {}),
        });
        if (!cancelled) setState({ loading: false, revisions: res?.data?.revisions || [] });
      } catch (e) {
        console.error(e);
        if (!cancelled) setState({ loading: false, error: e?.response?.data?.message || 'Failed to load history' });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [trigger.trigger_key, channel]);

  // The fixed AppBar sits at zIndex.drawer + 1, so this overlay drawer must render
  // at modal level or its header hides behind the bar.
  return (
    <Drawer anchor="right" open onClose={onClose} sx={{ zIndex: (t) => t.zIndex.modal }}>
      <Box sx={{ width: 440, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          {channel ? `${CHANNEL_LABELS[channel]} history` : 'Change history'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {trigger.label} <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>({trigger.trigger_key})</Box>
          {!channel && ' — all channels'}
        </Typography>
        {state.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : state.error ? (
          <Alert severity="error">{state.error}</Alert>
        ) : !state.revisions.length ? (
          <Typography variant="body2" color="text.secondary">
            No changes yet — {channel ? `the ${CHANNEL_LONG[channel]}` : 'every channel'} is on its
            registry default state and the copy has never been edited.
          </Typography>
        ) : (
          <List dense>
            {state.revisions.map((rev) => {
              const kind = rev.kind === 'content' ? 'content' : 'setting';
              if (kind === 'setting') {
                return (
                  <ListItem key={`setting-${rev.id}`} disableGutters divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip
                            label={rev.action}
                            size="small"
                            color={rev.action === 'enable' ? 'success' : rev.action === 'disable' ? 'error' : 'default'}
                            variant="outlined"
                          />
                          <Typography variant="body2">{CHANNEL_LABELS[rev.channel] || rev.channel}</Typography>
                        </Box>
                      }
                      secondary={`${rev.updated_by || 'unknown'} — ${rev.created_at ? new Date(rev.created_at).toLocaleString() : ''}`}
                    />
                  </ListItem>
                );
              }
              const chip = CONTENT_ACTION_CHIPS[rev.action] || { label: rev.action, color: 'default' };
              return (
                <ListItem key={`content-${rev.id}`} disableGutters divider sx={{ display: 'block', py: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip label={chip.label} size="small" color={chip.color} variant="outlined" />
                    <Typography variant="body2">{CHANNEL_LABELS[rev.channel] || rev.channel}</Typography>
                    <Chip label={rev.template_key} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                    {rev.enabled === false && (
                      <Tooltip title="This copy was saved switched off — Bidsy's built-in default was in use">
                        <Chip label="copy off" size="small" variant="outlined" sx={{ opacity: 0.7 }} />
                      </Tooltip>
                    )}
                  </Box>
                  {rev.action === 'reset' ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Custom copy removed — Bidsy's built-in default became active again.
                    </Typography>
                  ) : (
                    <Paper variant="outlined" sx={{ p: 1, mt: 0.75 }}>
                      {rev.subject != null && rev.subject !== '' && (
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }} component="div">
                          Subject: <TemplateText text={rev.subject} sx={{ display: 'inline', fontWeight: 400 }} />
                        </Typography>
                      )}
                      <TemplateText text={rev.body} />
                    </Paper>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {rev.updated_by || 'unknown'} — {rev.created_at ? new Date(rev.created_at).toLocaleString() : ''}
                  </Typography>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Drawer>
  );
}

/* ------------------------------------------------------------------ *
 * Main page
 * ------------------------------------------------------------------ */
export default function NotificationContent() {
  const { env } = useEnv();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('all'); // legacy fallback view only
  const [timingFilter, setTimingFilter] = useState('all');

  const [editing, setEditing] = useState(null);   // ChannelEditorDialog props
  const [confirmOff, setConfirmOff] = useState(null); // { trigger, channel }
  const [historyFor, setHistoryFor] = useState(null);
  const [publishMode, setPublishMode] = useState(null);
  const publishOpen = Boolean(publishMode);
  const [mutating, setMutating] = useState(false);
  const [snack, setSnack] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  // Monotonic id so a slow response for a previous environment can never
  // overwrite the state of the currently selected one.
  const fetchSeq = useRef(0);

  const showSnack = (s) => { setSnack(s); setSnackOpen(true); };

  const fetchData = async () => {
    const seq = ++fetchSeq.current;
    try {
      setLoading(true);
      setError(null);
      const res = await getNotificationTriggers();
      if (seq !== fetchSeq.current) return;
      setData(res?.data || null);
    } catch (e) {
      if (seq !== fetchSeq.current) return;
      console.error(e);
      setError(e?.response?.data?.message || 'Failed to load notification content');
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  };

  // Refetch when the dev/prod toggle changes — the content is environment-specific.
  useEffect(() => {
    setData(null);
    setEditing(null);
    setConfirmOff(null);
    setHistoryFor(null);
    setPublishMode(null);
    fetchData();
    // eslint-disable-next-line
  }, [env]);

  const isFallback = Boolean(data?.fallback);
  const editable = env === 'development' && !isFallback && Boolean(data?.editable);
  const triggers = data?.triggers || [];
  const categories = data?.categories || [];

  const filteredTriggers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return triggers.filter((t) => {
      if (!matchesTimingFilter(t.timing, timingFilter)) return false;
      if (!q) return true;
      const hay = [
        t.trigger_key, t.label, t.description,
        ...timingSearchText(t.timing),
        ...['alert', 'sms', 'email'].flatMap((ch) => t.channels?.[ch]?.template_keys || []),
      ].filter(Boolean).join('\n').toLowerCase();
      return hay.includes(q);
    });
  }, [triggers, search, timingFilter]);

  const replyKeywords = useMemo(
    () => (data?.reply_keywords || []).filter((row) => matchesTimingFilter(row.timing, timingFilter)),
    [data, timingFilter]
  );

  const grouped = useMemo(() => {
    const byCategory = new Map();
    for (const t of filteredTriggers) {
      if (!byCategory.has(t.category)) byCategory.set(t.category, []);
      byCategory.get(t.category).push(t);
    }
    const order = categories.length ? categories : [...byCategory.keys()];
    return order.filter((c) => byCategory.has(c)).map((c) => ({ category: c, triggers: byCategory.get(c) }));
  }, [filteredTriggers, categories]);

  const openEditor = (trigger, channel, intent) => {
    if (!editable || mutating || publishOpen) return;
    const info = trigger.channels?.[channel];
    if (!info || info.supported === false) return;
    setEditing({
      triggerKey: trigger.trigger_key,
      triggerLabel: trigger.label,
      channel,
      templateKeys: info.template_keys || [],
      channelInfo: info,
      intent,
    });
  };

  const openReplyEditor = (row) => {
    if (!editable || mutating || publishOpen) return;
    setEditing({
      triggerKey: null,           // registry-excluded — no channel state
      triggerLabel: row.trigger,
      channel: 'sms',
      templateKeys: [row.key],
      channelInfo: null,
      intent: 'edit',
    });
  };

  // Switch semantics (editor-first for never-configured channels):
  //  - ON on a never-configured channel opens the editor pre-filled with the
  //    registry default ("Enable with this copy" is the primary action).
  //  - ON on a previously-configured channel is an immediate PUT.
  //  - OFF always confirms first.
  const handleToggle = async (trigger, channel, info) => {
    if (!editable || mutating || publishOpen) return;
    if (info.enabled) {
      setConfirmOff({ trigger, channel });
      return;
    }
    const neverConfigured = !info.has_override && info.enabled_source === 'default';
    if (neverConfigured) {
      openEditor(trigger, channel, 'enable');
      return;
    }
    try {
      setMutating(true);
      await setTriggerChannel(trigger.trigger_key, channel, { send_enabled: true });
      showSnack({ severity: 'success', message: `${CHANNEL_LABELS[channel]} turned on for "${trigger.label}"` });
      fetchData();
    } catch (e) {
      console.error(e);
      showSnack({ severity: 'error', message: e?.response?.data?.message || 'Failed to turn the channel on' });
    } finally {
      setMutating(false);
    }
  };

  const handleConfirmOff = async () => {
    const { trigger, channel } = confirmOff;
    try {
      setMutating(true);
      await setTriggerChannel(trigger.trigger_key, channel, { send_enabled: false });
      showSnack({ severity: 'success', message: `${CHANNEL_LABELS[channel]} turned off for "${trigger.label}"` });
      setConfirmOff(null);
      fetchData();
    } catch (e) {
      console.error(e);
      showSnack({ severity: 'error', message: e?.response?.data?.message || 'Failed to turn the channel off' });
      setConfirmOff(null);
    } finally {
      setMutating(false);
    }
  };

  if (loading && !data) return <LoadingBlock height={320} />;
  if (error && !data) return <ErrorBlock message={error} onRetry={fetchData} />;

  const columns = ['Trigger', 'When it sends', 'In-app', 'SMS', 'Email'];

  return (
    <>
      <PageHeader
        title="Notification content"
        actions={env === 'development' && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              disabled={!editable || loading || Boolean(error) || mutating || Boolean(editing) || Boolean(confirmOff) || Boolean(historyFor) || publishOpen}
              onClick={() => setPublishMode('copy-to-dev')}
            >
              Copy prod to dev
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={!editable || loading || Boolean(error) || mutating || Boolean(editing) || Boolean(confirmOff) || Boolean(historyFor) || publishOpen}
              onClick={() => setPublishMode('publish')}
            >
              Publish to prod
            </Button>
          </Box>
        )}
      />

      {isFallback && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only: make and test changes in <strong>development</strong>, then use <strong>Publish to prod</strong> to review and publish them.
        </Alert>
      )}
      {!isFallback && !editable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only: make and test changes in <strong>development</strong>, then use <strong>Publish to prod</strong> to review and publish them.
        </Alert>
      )}

      {error && data && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={fetchData}>Retry</Button>}
        >
          {error} — showing the last loaded content.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isFallback ? 'Trigger, template key, or content…' : 'Trigger or template key…'}
            sx={{ minWidth: 260 }}
          />
          {isFallback && (
            <TextField
              select
              label="Channel"
              size="small"
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="all">All channels</MenuItem>
              <MenuItem value="alert">App alert</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
              <MenuItem value="email">Email</MenuItem>
            </TextField>
          )}
          <TextField
            select
            label="When it sends"
            size="small"
            value={timingFilter}
            onChange={(e) => setTimingFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All timing</MenuItem>
            {Object.entries(TIMING_MODES).map(([value, mode]) => (
              <MenuItem key={value} value={value}>{mode.label}</MenuItem>
            ))}
          </TextField>
          {isFallback ? (
            data?.counts && (
              <Chip
                variant="outlined"
                label={`${data.counts.total} templates • ${data.counts.sms} SMS • ${data.counts.alert} alerts • ${data.counts.email} email`}
              />
            )
          ) : (
            <Chip variant="outlined" label={`${triggers.length} triggers`} />
          )}
          <Chip
            label={data?.environment === 'production' ? 'PRODUCTION' : 'DEVELOPMENT'}
            color={data?.environment === 'production' ? 'default' : 'warning'}
            size="small"
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : isFallback ? (
          <LegacyReportView data={data} search={search} channelFilter={channelFilter} timingFilter={timingFilter} />
        ) : !grouped.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
            No triggers match your search.
          </Typography>
        ) : (
          <Table size="small" sx={STICKY_TABLE_SX}>
            <TableHead>
              <TableRow>
                {columns.map((c, i) => (
                  <TableCell key={c} sx={{ ...STICKY_HEAD_SX, fontWeight: 'bold', width: ['26%', '20%'][i] }}>
                    {c}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {grouped.map(({ category, triggers: rows }) => (
                <React.Fragment key={category}>
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ bgcolor: 'action.selected', fontWeight: 700 }}>
                      {CATEGORY_LABELS[category] || category}
                    </TableCell>
                  </TableRow>
                  {rows.map((t) => (
                    <TableRow key={t.trigger_key} sx={{ '& > td': { verticalAlign: 'top', py: 1.5 } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{t.description}</Typography>
                          </Box>
                          <Tooltip title="Change history (all channels)">
                            <IconButton size="small" onClick={() => setHistoryFor({ trigger: t, channel: null })} aria-label={`History for ${t.label}`}>
                              <HistoryIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell><TimingCell timing={t.timing} /></TableCell>
                      {['alert', 'sms', 'email'].map((ch) => (
                        <TableCell key={ch}>
                          <MatrixCell
                            trigger={t}
                            channel={ch}
                            editable={editable}
                            busy={mutating || publishOpen}
                            onOpenEditor={openEditor}
                            onToggle={handleToggle}
                            onHistory={(trigger, channel) => setHistoryFor({ trigger, channel })}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* SMS reply keywords — registry-excluded auto-replies, edited via the
          legacy per-key template routes. */}
      {!isFallback && replyKeywords.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>SMS reply keywords</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Automatic replies to texts users send us. These are protocol responses — they always send and
            have no on/off switch; only the wording can be changed.
          </Typography>
          <Table size="small">
            <TableBody>
              {replyKeywords.map((row) => (
                <TableRow key={row.id}>
                  <TableCell sx={{ width: '40%' }}>
                    <Typography variant="body2">{row.trigger}</Typography>
                  </TableCell>
                  <TableCell sx={{ width: '25%' }}><TimingCell timing={row.timing} /></TableCell>
                  <TableCell>
                    <Chip label={row.key} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                  </TableCell>
                  <TableCell align="right">
                    {editable && (
                      <Tooltip title={`Edit ${row.key}`}>
                        <IconButton size="small" onClick={() => openReplyEditor(row)} aria-label={`Edit ${row.key}`}>
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ------------------- Channel editor ------------------- */}
      {publishOpen && env === 'development' && (
        <NotificationPublishDialog
          mode={publishMode}
          onClose={() => {
            setPublishMode(null);
            if (publishMode === 'copy-to-dev') fetchData();
          }}
          onPublished={(response) => {
            setPublishMode(null);
            showSnack({ severity: 'success', message: response.message || `${response.data?.published_count || 0} notification changes ${publishMode === 'copy-to-dev' ? 'copied to development' : 'published to production'}.` });
            fetchData();
          }}
        />
      )}

      {editing && (
        <ChannelEditorDialog
          editing={editing}
          env={env}
          onClose={() => setEditing(null)}
          onRefresh={fetchData}
          onMutated={(message) => {
            showSnack({ severity: 'success', message });
            fetchData();
          }}
        />
      )}

      {/* ------------------- Switch-off confirmation ------------------- */}
      <Dialog open={Boolean(confirmOff)} onClose={mutating ? undefined : () => setConfirmOff(null)} maxWidth="sm" fullWidth>
        {confirmOff && (
          <>
            <DialogTitle>Turn off the {CHANNEL_LONG[confirmOff.channel]}?</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2">
                  Users will stop receiving the {CHANNEL_LONG[confirmOff.channel]} for{' '}
                  <strong>{confirmOff.trigger.label}</strong> on the <strong>development</strong> environment.
                  Copy is kept and the channel can be re-enabled at any time.
                </Typography>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOff(null)} disabled={mutating}>Back</Button>
              <Button color="error" variant="contained" onClick={handleConfirmOff} disabled={mutating}>
                {mutating ? 'Turning off…' : 'Turn off'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ------------------- History drawer ------------------- */}
      {historyFor && (
        <HistoryDrawer
          trigger={historyFor.trigger}
          channel={historyFor.channel}
          onClose={() => setHistoryFor(null)}
        />
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={(event, reason) => { if (reason !== 'clickaway') setSnackOpen(false); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnackOpen(false)}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </>
  );
}
